import type {
  EditableComponentsMap,
  PromptOptionsForEditableComponents,
  PromptOptionValue,
  PromptSuggestionsEnum,
  PromptSuggestionsValue,
  RedesignIssueType} from '@Components/ai-resdesign-prompt-box/ai-redesign-prompt-box.types';
import {
  AiModel,
  AspectRatio,
  DefaultPromptSuggestions,
  DesignPromptSuggestions,
  EditableComponent,
  EmailSingleRewrite,
  EmojiUsage,
  RedesignMood,
  RewriteMood,
  PromptOption,
  PromptOptionSelectorLayoutType,
  PromptSuggestions,
  RestaurantPromptSuggestions,
  Styles,
  MoodType,
} from '@Components/ai-resdesign-prompt-box/ai-redesign-prompt-box.types';
import type {Filter} from '@Components/checkbox-filters/checkbox-filters.types';
import {CHECKBOX_TYPE} from '@Components/checkbox';
import {IconSize} from '@Components/icon-v2/icon.types';
import {TextSize} from '@Components/text';
import type {IconProps} from '@Components/icon-v2';

export const RECENT_PROMPT_DISPLAY_LENGTH = 30;
export const RECENT_PROMPT_DISPLAY_LENGTH_SHORT = 27;
export const RECENT_PROMPT_SUGGESTION_PILL_KEY = 'recent-prompt';
export const RECENT_PROMPT_SUGGESTION_PILL_ICON = 'icon-history';
export const EVENT_PROMPT_PILL_KEY = 'event-prompt-pill';

export const getDefaultPromptOptionValue = (option: PromptOption, moodType: MoodType = MoodType.REDESIGN): PromptOptionValue => {
  switch (option) {
    case PromptOption.MOOD:
      return moodType === MoodType.REWRITE ? RewriteMood.Professional : RedesignMood.Formal;
    case PromptOption.EMOJI_USAGE:
      return EmojiUsage.Disabled;
    case PromptOption.AI_MODEL:
      return AiModel.Auto;
    case PromptOption.STYLES:
      return Styles.Photographic;
    case PromptOption.ASPECT_RATIO:
      return AspectRatio.Square;
  }
};

export const getPromptOptionValueContent = (value: PromptOptionValue | null): string => {
  if (!value) {
    return '';
  }

  switch (value) {
    case EmojiUsage.Disabled:
      return `👎 ${window.i18next.t('pmwjs_no')}`;
    case EmojiUsage.Enabled:
      return `👍 ${window.i18next.t('pmwjs_yes')}`;
    case RedesignMood.Formal:
      return `👔 ${window.i18next.t('pmwjs_formal')}`;
    case RedesignMood.Bold:
      return `🔥 ${window.i18next.t('pmwjs_bold')}`;
    case RedesignMood.Minimal:
      return `🌱 ${window.i18next.t('pmwjs_minimal')}`;
    case RedesignMood.Elegant:
      return `👒 ${window.i18next.t('pmwjs_elegant')}`;
    case RedesignMood.Vibrant:
      return `🌈 ${window.i18next.t('pmwjs_vibrant')}`;
    case RedesignMood.Modern:
      return `⚡ ${window.i18next.t('pmwjs_modern_style')}`;
    case RedesignMood.Classic:
      return `📜 ${window.i18next.t('pmwjs_classic')}`;
    case RedesignMood.Creative:
      return `🎨 ${window.i18next.t('pmwjs_creative')}`;
    case RedesignMood.Playful:
      return `🪩 ${window.i18next.t('pmwjs_playful')}`;
    case RedesignMood.Sleek:
      return `💎 ${window.i18next.t('pmwjs_sleek')}`;
    case RewriteMood.Casual:
      return `😊 ${window.i18next.t('pmwjs_casual')}`;
    case RewriteMood.Professional:
      return `💼 ${window.i18next.t('pmwjs_professional')}`;
    case RewriteMood.Bold:
      return `🔥 ${window.i18next.t('pmwjs_bold')}`;
    case RewriteMood.Elegant:
      return `👒 ${window.i18next.t('pmwjs_elegant')}`;
    case RewriteMood.Playful:
      return `🎉 ${window.i18next.t('pmwjs_playful')}`;
    case RewriteMood.Engaging:
      return `✨ ${window.i18next.t('pmwjs_engaging')}`;
    case RewriteMood.Creative:
      return `🎨 ${window.i18next.t('pmwjs_creative')}`;
    case AiModel.Flux:
      return window.i18next.t('pmwjs_flux');
    case AiModel.Stability:
      return window.i18next.t('pmwjs_stability');
    case AiModel.Auto:
      return window.i18next.t('pmwjs_auto');
    case AiModel.NanoBanana:
      return window.i18next.t('pmwjs_nano_banana');
    case AiModel.StabilityUltra:
      return window.i18next.t('pmwjs_stability_ultra');
    case Styles.Photographic:
      return `📷 ${window.i18next.t('pmwjs_photographic')}`;
    case Styles.DigitalArt:
      return `💻 ${window.i18next.t('pmwjs_digital_art')}`;
    case Styles.Model3d:
      return `📦 ${window.i18next.t('pmwjs_three_d_model')}`;
    case Styles.Cinematic:
      return `🎬 ${window.i18next.t('pmwjs_cinematic')}`;
    case Styles.NeonPunk:
      return `🤖 ${window.i18next.t('pmwjs_futuristic')}`;
    case Styles.LineArt:
      return `✏️ ${window.i18next.t('pmwjs_line_art')}`;
    case Styles.FantasyArt:
      return `🐉 ${window.i18next.t('pmwjs_fantasy')}`;
    case Styles.Anime:
      return `👩‍🎤 ${window.i18next.t('pmwjs_anime')}`;
    case Styles.PixelArt:
      return `👾 ${window.i18next.t('pmwjs_pixel_art')}`;
    case Styles.AnalogFilm:
      return `📻 ${window.i18next.t('pmwjs_vintage')}`;
    case Styles.ComicBook:
      return `🦸🏻‍♂️ ${window.i18next.t('pmwjs_comic')}`;
    case AspectRatio.Square:
      return window.i18next.t('pmwjs_square');
    case AspectRatio.Portrait:
      return window.i18next.t('pmwjs_portrait');
    case AspectRatio.LandScape:
      return window.i18next.t('pmwjs_landscape');
  }
};

export const getIconPropsForPromptOptionValue = (value: PromptOptionValue | null): IconProps|undefined => {
  if (!value) {
    return;
  }
  switch (value) {
    case AspectRatio.Square:
      return {
        icon: `icon-square`,
        size: IconSize.SIZE_ICON_16,
      };
    case AspectRatio.LandScape:
      return {
        icon: `icon-landscape`,
        size: IconSize.SIZE_ICON_16,
      };
    case AspectRatio.Portrait:
      return {
        icon: `icon-portrait`,
        size: IconSize.SIZE_ICON_16,
      }
    default:
      return;
  }
}

export const getPromptOptionValueDescription = (value: PromptOptionValue): string => {
  switch (value) {
    case AiModel.Flux:
      return window.i18next.t('pmwjs_ai_model_flux_desc');
    case AiModel.Stability:
      return window.i18next.t('pmwjs_ai_model_stability_desc');
    case AiModel.StabilityUltra:
      return window.i18next.t('pmwjs_ai_model_stability_ultra_desc');
    case AiModel.NanoBanana:
      return window.i18next.t('pmwjs_ai_model_nano_banana_desc');
    case AiModel.Auto:
      return window.i18next.t('pmwjs_ai_model_auto_desc');
    default:
      return '';
  }
};

export const getCreditCostForModel = (value: PromptOptionValue): number => {
  switch (value) {
    case AiModel.StabilityUltra:
      return 3;
    case AiModel.NanoBanana:
      return 2;
    default:
      return 1;
  }
}

export const getPromptSuggestionsPillTitle = (value: PromptSuggestionsValue | null): string => {
  if (!value) {
    return '';
  }

  switch (value) {
    case DefaultPromptSuggestions.CHURCH_NEWLETTER:
      return `📖 ${window.i18next.t('pmwjs_church_newsletter')}`;
    case DefaultPromptSuggestions.RESTAURANT_DAILY_SPECIALS:
      return `🍽️ ${window.i18next.t('pmwjs_daily_specials')}`;
    case DefaultPromptSuggestions.SALE:
      return `🛍️ ${window.i18next.t('pmwjs_store_sale')}`;
    case DefaultPromptSuggestions.SCHOOL_NEWSLETTER:
      return `🏫 ${window.i18next.t('pmwjs_school_newsletter')}`;
    case RestaurantPromptSuggestions.BAR_MENU:
      return `🍹 ${window.i18next.t('pmwjs_bar_menu')}`;
    case RestaurantPromptSuggestions.HAPPY_HOUR:
      return `🎉 ${window.i18next.t('pmwjs_happy_hour')}`;
    case RestaurantPromptSuggestions.COCKTAIL_HOUR:
      return `🍸 ${window.i18next.t('pmwjs_cocktail_hour')}`;
    case RestaurantPromptSuggestions.CAFE_DAILY_SPECIALS:
      return `☕ ${window.i18next.t('pmwjs_cafe_daily_specials')}`;
    case EmailSingleRewrite.CHURCH_EVEMTS:
      return `📖 ${window.i18next.t('pmwjs_church_events')}`;
    case EmailSingleRewrite.SALE:
      return `🛍️ ${window.i18next.t('pmwjs_store_sale')}`;
    case EmailSingleRewrite.DAILY_SPECIALS:
      return `☕ ${window.i18next.t('pmwjs_daily_specials')}`;
    case EmailSingleRewrite.SUNDAY_SERVICE:
      return `⛪ ${window.i18next.t('pmwjs_sunday_service')}`;
    case DesignPromptSuggestions.SAVE_THE_DATE:
      return `💌 ${window.i18next.t('pmwjs_save_the_date')}`;
    case DesignPromptSuggestions.BIRTHDAY_INVITE:
      return `🎉 ${window.i18next.t('pmwjs_birthday_invite')}`;
    case DesignPromptSuggestions.SONG_RELEASE:
      return `🎵 ${window.i18next.t('pmwjs_song_release')}`;
    case DesignPromptSuggestions.HAPPY_HOUR_PROMOTE:
      return `🍸 ${window.i18next.t('pmwjs_happy_hour')}`;
    case DesignPromptSuggestions.SUNDAY_SERVICE:
      return `⛪ ${window.i18next.t('pmwjs_sunday_service')}`;
    case DesignPromptSuggestions.LUNCH_DEALS:
      return `🍽️ ${window.i18next.t('pmwjs_lunch_deals')}`;
    case DesignPromptSuggestions.DJ_NIGHT:
      return `🎧 ${window.i18next.t('pmwjs_dj_night')}`;
    case DesignPromptSuggestions.DANCE_PARTY:
      return `💃 ${window.i18next.t('pmwjs_dance_party')}`;
    case DesignPromptSuggestions.SUMMER_CAMP:
      return `🏕️ ${window.i18next.t('pmwjs_summer_camp')}`;
    case DesignPromptSuggestions.BACK_TO_SCHOOL:
      return `🎒 ${window.i18next.t('pmwjs_back_to_school')}`;
    case DesignPromptSuggestions.CHARITY_FUNDRAISER:
      return `❤️ ${window.i18next.t('pmwjs_charity_fundraiser')}`;
    case DesignPromptSuggestions.RETAIL_SALE:
      return `🛍️ ${window.i18next.t('pmwjs_retail_sale')}`;
    case DesignPromptSuggestions.BIBLE_STUDY:
      return `📖 ${window.i18next.t('pmwjs_bible_study')}`;
    case DesignPromptSuggestions.SUNDAY_BRUNCH:
      return `🥞 ${window.i18next.t('pmwjs_sunday_brunch')}`;
    case DesignPromptSuggestions.FOOD_DELIVERY:
      return `🚚 ${window.i18next.t('pmwjs_food_delivery')}`;
    default:
      return '';
  }
};

export const getPromptSuggestionsReplacementPrompt = (value: PromptSuggestionsValue | null): string => {
  if (!value) {
    return '';
  }

  switch (value) {
    case DefaultPromptSuggestions.CHURCH_NEWLETTER:
      return window.i18next.t('pmwjs_email_redesign_for_church_newsletter');
    case DefaultPromptSuggestions.RESTAURANT_DAILY_SPECIALS:
      return window.i18next.t('pmwjs_email_redesign_for_restaurant_daily_specials');
    case DefaultPromptSuggestions.SALE:
      return window.i18next.t('pmwjs_email_redesign_for_sale');
    case DefaultPromptSuggestions.SCHOOL_NEWSLETTER:
      return window.i18next.t('pmwjs_email_redesign_for_school_newsletter');
    case RestaurantPromptSuggestions.BAR_MENU:
      return window.i18next.t('pmwjs_redesign_this_for_my_bar_menu');
    case RestaurantPromptSuggestions.HAPPY_HOUR:
      return window.i18next.t('pmwjs_redesign_this_for_my_happy_hour');
    case RestaurantPromptSuggestions.COCKTAIL_HOUR:
      return window.i18next.t('pmwjs_redesign_this_for_my_cocktail_hour');
    case RestaurantPromptSuggestions.CAFE_DAILY_SPECIALS:
      return window.i18next.t('pmwjs_redesign_this_for_my_cafe_daily_specials');
    case EmailSingleRewrite.CHURCH_EVEMTS:
      return window.i18next.t('pmwjs_email_rewrite_for_church_events');
    case EmailSingleRewrite.SALE:
      return window.i18next.t('pmwjs_email_rewrite_for_store_sale');
    case EmailSingleRewrite.DAILY_SPECIALS:
      return window.i18next.t('pmwjs_email_rewrite_for_daily_specials');
    case EmailSingleRewrite.SUNDAY_SERVICE:
      return window.i18next.t('pmwjs_email_rewrite_for_sunday_service');
    case DesignPromptSuggestions.SAVE_THE_DATE:
      return window.i18next.t('pmwjs_rewrite_for_save_the_date');
    case DesignPromptSuggestions.BIRTHDAY_INVITE:
      return window.i18next.t('pmwjs_rewrite_for_birthday_invite');
    case DesignPromptSuggestions.SONG_RELEASE:
      return window.i18next.t('pmwjs_rewrite_for_song_release');
    case DesignPromptSuggestions.HAPPY_HOUR_PROMOTE:
      return window.i18next.t('pmwjs_rewrite_for_happy_hour');
    case DesignPromptSuggestions.SUNDAY_SERVICE:
      return window.i18next.t('pmwjs_rewrite_for_sunday_service');
    case DesignPromptSuggestions.LUNCH_DEALS:
      return window.i18next.t('pmwjs_rewrite_for_lunch_deals');
    case DesignPromptSuggestions.DJ_NIGHT:
      return window.i18next.t('pmwjs_rewrite_for_dj_night');
    case DesignPromptSuggestions.DANCE_PARTY:
      return window.i18next.t('pmwjs_rewrite_for_dance_party');
    case DesignPromptSuggestions.SUMMER_CAMP:
      return window.i18next.t('pmwjs_rewrite_for_summer_camp');
    case DesignPromptSuggestions.BACK_TO_SCHOOL:
      return window.i18next.t('pmwjs_rewrite_for_back_to_school');
    case DesignPromptSuggestions.CHARITY_FUNDRAISER:
      return window.i18next.t('pmwjs_rewrite_for_charity_fundraiser');
    case DesignPromptSuggestions.RETAIL_SALE:
      return window.i18next.t('pmwjs_rewrite_for_retail_sale');
    case DesignPromptSuggestions.BIBLE_STUDY:
      return window.i18next.t('pmwjs_rewrite_for_bible_study');
    case DesignPromptSuggestions.SUNDAY_BRUNCH:
      return window.i18next.t('pmwjs_rewrite_for_sunday_brunch');
    case DesignPromptSuggestions.FOOD_DELIVERY:
      return window.i18next.t('pmwjs_rewrite_for_food_delivery');
    default:
      return '';
  }
};

export const getPromptOptionTitle = (option: PromptOption, moodType: MoodType): string => {
  switch (option) {
    case PromptOption.MOOD:
      return moodType === MoodType.REDESIGN ? window.i18next.t('pmwjs_mood') : window.i18next.t('pmwjs_tone');
    case PromptOption.EMOJI_USAGE:
      return window.i18next.t('pmwjs_add_emojis');
    case PromptOption.AI_MODEL:
      return window.i18next.t('pmwjs_ai_model');
    case PromptOption.STYLES:
      return window.i18next.t('pmwjs_style');
    case PromptOption.ASPECT_RATIO:
      return window.i18next.t('pmwjs_aspect_ratio');
  }
};

export const getPromptOptionEnum = (option: PromptOption, moodType: MoodType = MoodType.REDESIGN): typeof EmojiUsage | typeof RedesignMood | typeof RewriteMood | typeof AiModel | typeof Styles | typeof AspectRatio => {
  switch (option) {
    case PromptOption.EMOJI_USAGE:
      return EmojiUsage;
    case PromptOption.MOOD:
      return moodType === MoodType.REWRITE ? RewriteMood : RedesignMood;
    case PromptOption.STYLES:
      return Styles;
    case PromptOption.AI_MODEL:
      return AiModel;
    case PromptOption.ASPECT_RATIO:
      return AspectRatio;
  }
};

export const getPromptSuggestionsEnum = (promptSuggestions: PromptSuggestions): PromptSuggestionsEnum => {
  switch (promptSuggestions) {
    case PromptSuggestions.RESTAURANT:
      return RestaurantPromptSuggestions;
    case PromptSuggestions.EMAIL_SINGLE_REWRITE:
      return EmailSingleRewrite;
    case PromptSuggestions.DESIGN:
      return DesignPromptSuggestions;
    case PromptSuggestions.DEFAULT:
    default:
      return DefaultPromptSuggestions;
  }
};

export const getPromptOptionSelectorLayoutType = (option: PromptOption): PromptOptionSelectorLayoutType => {
  switch (option) {
    case PromptOption.EMOJI_USAGE:
    case PromptOption.MOOD:
    case PromptOption.STYLES:
    case PromptOption.ASPECT_RATIO:
      return PromptOptionSelectorLayoutType.PILLS;
    case PromptOption.AI_MODEL:
      return PromptOptionSelectorLayoutType.CARDS;
  }
};

export const getEditableComponentCheckboxContent = (
  editableComponent: EditableComponent,
  editableComponentsMap: EditableComponentsMap,
  labelIconClassName: string,
  textClassName: string
): Filter => {
  switch (editableComponent) {
    case EditableComponent.IMAGE:
      return {
        label: window.i18next.t('pmwjs_images'),
        labelIcon: 'icon-image',
        dataValue: EditableComponent.IMAGE,
        isChecked: editableComponentsMap[EditableComponent.IMAGE] ?? false,
        checkboxType: CHECKBOX_TYPE.PRIMARY,
        checkboxSize: IconSize.SIZE_ICON_16,
        className: `spacing-m-b-0 spacing-p-t-2 spacing-p-b-2`,
        labelIconClassName: labelIconClassName,
        textSize: TextSize.XXSMALL,
        textClassName: textClassName,
      };
    case EditableComponent.TEXT:
      return {
        label: window.i18next.t('pmwjs_text'),
        labelIcon: 'icon-text',
        dataValue: EditableComponent.TEXT,
        isChecked: editableComponentsMap[EditableComponent.TEXT] ?? false,
        checkboxType: CHECKBOX_TYPE.PRIMARY,
        checkboxSize: IconSize.SIZE_ICON_16,
        className: `spacing-m-b-0 spacing-p-t-2 spacing-p-b-2`,
        labelIconClassName: labelIconClassName,
        textSize: TextSize.XXSMALL,
        textClassName: textClassName,
      };
    case EditableComponent.STYLES:
      return {
        label: window.i18next.t('pmwjs_styles'),
        labelIcon: 'icon-color-palette',
        dataValue: EditableComponent.STYLES,
        isChecked: editableComponentsMap[EditableComponent.STYLES] ?? false,
        checkboxType: CHECKBOX_TYPE.PRIMARY,
        checkboxSize: IconSize.SIZE_ICON_16,
        className: `spacing-m-b-0 spacing-p-t-2 spacing-p-b-2`,
        labelIconClassName: labelIconClassName,
        textSize: TextSize.XXSMALL,
        textClassName: textClassName,
      };
  }
};

export const getPromptOptionsToUse = (editableComponentMap: EditableComponentsMap, promptOptionsForEditableComponents: PromptOptionsForEditableComponents): PromptOption[] => {
  const promptOptions: PromptOption[] = [];
  const keys = Object.keys(editableComponentMap) as EditableComponent[];

  if (keys.length === 1) {
    return promptOptionsForEditableComponents[keys[0]] ?? [];
  }

  keys.forEach((editableComponent) => {
    if (editableComponentMap[editableComponent]) {
      promptOptions.push(...(promptOptionsForEditableComponents[editableComponent] ?? []));
    }
  });

  return Array.from(new Set(promptOptions));
};

export class AiRedesignPromptBoxIssue extends Error {
  icon: string;
  type: RedesignIssueType;

  constructor(message: string, icon: string, type: RedesignIssueType) {
    super(message);
    this.icon = icon;
    this.type = type;
  }
}
